import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { api } from '../api/client';
import type { DashboardSummary } from '../api/types';
import { Card, Empty, ErrorAlert, Spinner, Stat } from '../components/ui';
import { useCurrentRouter } from '../lib/current-router';
import { formatBytes, formatDuration, formatNumber, formatRelative } from '../lib/format';

export function DashboardPage() {
  const current = useCurrentRouter();
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', 'summary', current.routerId],
    queryFn: () =>
      api.get<DashboardSummary>('/dashboard/summary', { routerId: current.routerId || undefined }),
    enabled: !current.loading,
    refetchInterval: 60_000,
  });

  if (isLoading || current.loading) {
    return (
      <main className="page">
        <Spinner />
      </main>
    );
  }

  if (error || !data) {
    return (
      <main className="page">
        <ErrorAlert error={error ?? new Error('No se pudo cargar el resumen')} />
      </main>
    );
  }

  const totalTraffic = data.traffic.bytesIn + data.traffic.bytesOut;

  return (
    <main className="page">
      <div className="page-header">
        <div>
          <h1>Dashboard</h1>
          <p>
            {current.router
              ? `Estado de ${current.router.name} y de los usuarios que gestionas en él.`
              : 'Estado general de los routers y los usuarios que gestionas.'}
          </p>
        </div>
      </div>

      <div className="grid grid-stats" style={{ marginBottom: 18 }}>
        <Stat
          label="Routers"
          value={formatNumber(data.routers.total)}
          hint={
            data.routers.online === data.routers.total
              ? 'Todos en línea'
              : `${data.routers.online} en línea · ${data.routers.offline + data.routers.authError} con problemas`
          }
        />
        <Stat
          label="Sesiones activas"
          value={formatNumber(data.activeSessions.count)}
          hint={`${formatNumber(data.activeSessions.hotspot)} HotSpot · ${formatNumber(data.activeSessions.pppoe)} PPPoE · sondeo ${formatRelative(data.activeSessions.asOf)}`}
        />
        <Stat
          label="Usuarios HotSpot"
          value={formatNumber(data.hotspotUsers.total)}
          hint={`${formatNumber(data.hotspotUsers.manual)} manuales · ${formatNumber(data.hotspotUsers.vouchers)} vouchers · ${formatNumber(data.hotspotUsers.active)} activos`}
        />
        <Stat
          label="Clientes PPPoE"
          value={formatNumber(data.pppoeClients.total)}
          hint={`${formatNumber(data.pppoeClients.active)} activos · ${formatNumber(data.pppoeClients.disabled + data.pppoeClients.expired)} suspendidos`}
        />
        <Stat
          label="Tráfico acumulado"
          value={formatBytes(totalTraffic)}
          hint={`↓ ${formatBytes(data.traffic.bytesOut)} · ↑ ${formatBytes(data.traffic.bytesIn)}`}
        />
        <Stat label="Creados hoy" value={formatNumber(data.createdToday)} hint="Usuarios, vouchers y clientes" />
      </div>

      {data.hotspotUsers.pendingSync > 0 && (
        <div className="alert warn">
          <strong>
            {data.hotspotUsers.pendingSync} usuario(s) existen en la base pero no en su router.
          </strong>
          <div style={{ marginTop: 6 }}>
            Se crearon mientras el router no respondía. El mantenimiento los reintenta cada 5
            minutos, o puedes forzarlo desde{' '}
            <Link to="/hotspot-users?status=PENDING_SYNC">Usuarios HotSpot</Link>.
          </div>
        </div>
      )}

      {data.pppoeClients.pendingSync > 0 && (
        <div className="alert warn">
          <strong>
            {data.pppoeClients.pendingSync} cliente(s) PPPoE existen en la base pero no en su router.
          </strong>
          <div style={{ marginTop: 6 }}>
            El mantenimiento los reintenta cada 5 minutos, o puedes forzarlo desde{' '}
            <Link to="/pppoe-clients?status=PENDING_SYNC">Clientes PPPoE</Link>.
          </div>
        </div>
      )}

      {data.routersNeedingAttention.length > 0 && (
        <div className="alert error" style={{ marginBottom: 18 }}>
          <strong>Routers que requieren atención</strong>
          <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
            {data.routersNeedingAttention.map((router) => (
              <li key={router.id}>
                <Link to="/routers">{router.name}</Link>
                {' — '}
                {router.status === 'AUTH_ERROR' ? 'credencial rechazada' : 'sin conexión'}
                {router.lastError && <span className="detail">{router.lastError}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-two">
        <Card title="Sesiones recientes">
          {data.recentSessions.length === 0 ? (
            <Empty>Todavía no se ha registrado ninguna sesión.</Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Usuario</th>
                    <th>Router</th>
                    <th>IP</th>
                    <th className="num">Tiempo</th>
                    <th className="num">Consumo</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentSessions.map((session, index) => (
                    <tr key={`${session.username}-${session.startedAt}-${index}`}>
                      <td className="mono">{session.username}</td>
                      <td>{session.routerName}</td>
                      <td className="mono faint">{session.ipAddress ?? '—'}</td>
                      <td className="num">{formatDuration(session.uptimeSeconds)}</td>
                      <td className="num">{formatBytes(session.bytesIn + session.bytesOut)}</td>
                      <td>
                        {session.endedAt ? (
                          <span className="faint">cerrada {formatRelative(session.endedAt)}</span>
                        ) : (
                          <span className="badge ok">en línea</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card title="Usuarios por plan">
          {data.byPlan.length === 0 ? (
            <Empty>Aún no hay usuarios asignados a ningún plan.</Empty>
          ) : (
            <div className="card-body">
              {data.byPlan.map((row) => {
                const max = Math.max(...data.byPlan.map((item) => item.count));
                const width = max > 0 ? Math.round((row.count / max) * 100) : 0;
                return (
                  <div key={`${row.service}-${row.planId}`} style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span>
                        {row.planName}
                        {row.service === 'PPPOE' && <span className="faint"> · PPPoE</span>}
                      </span>
                      <strong>{formatNumber(row.count)}</strong>
                    </div>
                    <div
                      style={{
                        height: 6,
                        borderRadius: 3,
                        background: 'var(--bg-subtle)',
                        overflow: 'hidden',
                      }}
                    >
                      <div style={{ width: `${width}%`, height: '100%', background: 'var(--accent)' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </main>
  );
}
